import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  IndianRupee, Calendar, Star, Send, Upload, X, ImageIcon, Trash2, Plus,
  Sparkles, Save, MessageSquare, ChevronLeft, BarChart3, CheckCircle2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { compressManyImages } from '../../utils/imageCompress';
import { HOTEL_FACILITIES } from '../../data/hospitalityLists';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const SectionTitle = ({ title, subtitle, action }) => (
  <div className="flex items-start justify-between gap-4 mb-6">
    <div>
      <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-black">{title}</h2>
      {subtitle && <p className="text-sm text-gray-600 mt-1">{subtitle}</p>}
    </div>
    {action}
  </div>
);

// ========================================================================
// CALENDAR / INVENTORY — month grid per room type + bulk edit
// ========================================================================
export const CalendarSection = ({ rooms, reloadRooms }) => {
  const [selectedRoom, setSelectedRoom] = useState(rooms[0]?._id || null);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [editDate, setEditDate] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [editStock, setEditStock] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulk, setBulk] = useState({ start: '', end: '', price: '', stock: '', restrict: 'all', closed: '' });

  useEffect(() => {
    if (!selectedRoom && rooms.length > 0) setSelectedRoom(rooms[0]._id);
  }, [rooms, selectedRoom]);

  const room = rooms.find((r) => r._id === selectedRoom);

  const daysInMonth = useMemo(() => {
    if (!month) return [];
    const [y, m] = month.split('-').map(Number);
    const last = new Date(y, m, 0).getDate();
    return Array.from({ length: last }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`);
  }, [month]);

  const inv = room?.inventory_overrides || {};

  const submit = async () => {
    if (!editDate || !room) return;
    try {
      await axios.put(
        `${API}/hotel/inventory`,
        { room_type_id: room._id, date: editDate, price: Number(editPrice) || room.base_price, available: Number(editStock) || 0 },
        { withCredentials: true },
      );
      toast.success(`${editDate} updated · auto-approved`);
      setEditDate(null);
      reloadRooms();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const submitBulk = async () => {
    if (!room || !bulk.start || !bulk.end) return;
    if (!bulk.price && !bulk.stock && !bulk.closed) {
      toast.error('Provide price, stock, or open/close');
      return;
    }
    const payload = {
      room_type_id: room._id,
      start_date: bulk.start,
      end_date: bulk.end,
      weekdays_only: bulk.restrict === 'weekdays',
      weekends_only: bulk.restrict === 'weekends',
    };
    if (bulk.price) payload.price = Number(bulk.price);
    if (bulk.stock !== '') payload.available = Number(bulk.stock);
    if (bulk.closed === 'close') payload.closed = true;
    else if (bulk.closed === 'open') payload.closed = false;
    try {
      const { data } = await axios.put(`${API}/hotel/inventory/bulk`, payload, { withCredentials: true });
      toast.success(`${data.message} · auto-approved`);
      setBulkOpen(false);
      setBulk({ start: '', end: '', price: '', stock: '', restrict: 'all', closed: '' });
      reloadRooms();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk update failed');
    }
  };

  if (rooms.length === 0) return (
    <div data-testid="calendar-section">
      <SectionTitle title="Calendar" subtitle="No rooms yet — add room types first." />
    </div>
  );

  return (
    <div data-testid="calendar-section">
      <SectionTitle
        title="Calendar"
        subtitle="Click any date to edit, or use Bulk Edit to apply across a range."
        action={
          <button
            onClick={() => setBulkOpen(true)}
            className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-black hover:bg-gray-800 text-white text-sm font-bold transition-colors"
            data-testid="calendar-bulk-open-btn"
          >
            <Sparkles className="h-3.5 w-3.5" /> Bulk Edit
          </button>
        }
      />
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <select
          value={selectedRoom || ''}
          onChange={(e) => setSelectedRoom(e.target.value)}
          className="px-4 py-2 rounded-full border border-gray-200 focus:border-[#FF0059] outline-none text-sm font-bold bg-white"
          data-testid="calendar-room-select"
        >
          {rooms.map((r) => {
            const label = `${r.name} (₹${r.base_price})`;
            return <option key={r._id} value={r._id} label={label}>{label}</option>;
          })}
        </select>
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="px-4 py-2 rounded-full border border-gray-200 focus:border-[#FF0059] outline-none text-sm font-bold bg-white"
          data-testid="calendar-month-picker"
        />
        <button
          onClick={() => setBulkOpen(true)}
          className="sm:hidden ml-auto px-4 py-2 rounded-full bg-black text-white text-xs font-bold"
          data-testid="calendar-bulk-open-btn-mobile"
        >
          Bulk Edit
        </button>
      </div>
      <div className="inline-flex items-center gap-1.5 px-3 py-1 mb-5 rounded-full bg-green-50 border border-green-200 text-[11px] font-bold text-green-700">
        <CheckCircle2 className="h-3 w-3" /> Changes auto-approved · go live instantly
      </div>

      <div className="grid grid-cols-7 gap-2 bg-white border border-gray-200 rounded-2xl p-4" data-testid="calendar-grid">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
          <div key={d} className="text-[10px] font-bold uppercase tracking-wider text-gray-400 text-center py-1">{d}</div>
        ))}
        {(() => {
          const [y, m] = month.split('-').map(Number);
          const firstDay = new Date(y, m - 1, 1).getDay() || 7; // Mon=1 .. Sun=7
          const pad = firstDay - 1;
          return Array.from({ length: pad }).map((_, i) => <div key={`pad-${i}`} />);
        })()}
        {daysInMonth.map((date) => {
          const o = inv[date];
          const price = o?.price ?? room.base_price;
          const stock = o?.available ?? room.total_rooms;
          const isLow = stock <= 0;
          return (
            <button
              key={date}
              onClick={() => {
                setEditDate(date);
                setEditPrice(String(price));
                setEditStock(String(stock));
              }}
              className={`aspect-square p-1.5 rounded-lg border text-left text-[11px] transition-colors ${isLow ? 'border-red-200 bg-red-50' : 'border-gray-100 hover:border-[#FF0059] hover:bg-[#FFE5EE]'}`}
              data-testid={`calendar-day-${date}`}
            >
              <p className="font-black text-black">{date.slice(8)}</p>
              <p className="text-[10px] text-gray-500 truncate">₹{price}</p>
              <p className={`text-[10px] font-bold ${isLow ? 'text-red-600' : 'text-[#FF0059]'}`}>{stock} left</p>
            </button>
          );
        })}
      </div>

      {editDate && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4" onClick={() => setEditDate(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="calendar-edit-modal">
            <h3 className="text-xl font-black text-black mb-1">{editDate}</h3>
            <p className="text-xs text-gray-500 mb-4">{room.name}</p>
            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">Price (₹)</label>
            <input type="number" value={editPrice} onChange={(e) => setEditPrice(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#FF0059] outline-none mb-3" data-testid="calendar-price-input" />
            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">Available rooms</label>
            <input type="number" value={editStock} onChange={(e) => setEditStock(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#FF0059] outline-none mb-4" data-testid="calendar-stock-input" />
            <div className="flex items-center gap-2 justify-end">
              <button onClick={() => setEditDate(null)} className="px-4 py-2 rounded-full border border-gray-200 text-sm font-bold">Cancel</button>
              <button onClick={submit} className="px-4 py-2 rounded-full bg-[#FF0059] hover:bg-[#CC0047] text-white text-sm font-bold" data-testid="calendar-save-btn">Save</button>
            </div>
          </div>
        </div>
      )}

      {bulkOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4" onClick={() => setBulkOpen(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="calendar-bulk-modal">
            <h3 className="text-xl font-black text-black mb-1 flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#FF0059]" /> Bulk Edit</h3>
            <p className="text-xs text-gray-500 mb-4">Update price &amp; availability for many dates in one go.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">From</label>
                <input type="date" value={bulk.start} onChange={(e) => setBulk({ ...bulk, start: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#FF0059] outline-none text-sm" data-testid="bulk-start-input" />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">To</label>
                <input type="date" value={bulk.end} onChange={(e) => setBulk({ ...bulk, end: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#FF0059] outline-none text-sm" data-testid="bulk-end-input" />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">Price (₹)</label>
                <input type="number" placeholder="optional" value={bulk.price} onChange={(e) => setBulk({ ...bulk, price: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#FF0059] outline-none text-sm" data-testid="bulk-price-input" />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">Stock</label>
                <input type="number" placeholder="optional" value={bulk.stock} onChange={(e) => setBulk({ ...bulk, stock: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#FF0059] outline-none text-sm" data-testid="bulk-stock-input" />
              </div>
            </div>
            <div className="mt-3">
              <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1.5">Apply on</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'all', label: 'All days' },
                  { id: 'weekdays', label: 'Weekdays only' },
                  { id: 'weekends', label: 'Weekends only' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setBulk({ ...bulk, restrict: opt.id })}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${bulk.restrict === opt.id ? 'bg-[#FF0059] text-white' : 'bg-white border border-gray-200 text-gray-700 hover:border-[#FF0059]'}`}
                    data-testid={`bulk-restrict-${opt.id}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-3">
              <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1.5">Open / Close rooms</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: '', label: 'No change' },
                  { id: 'close', label: 'Close (stop sales)' },
                  { id: 'open', label: 'Re-open' },
                ].map((opt) => (
                  <button
                    key={opt.id || 'none'}
                    type="button"
                    onClick={() => setBulk({ ...bulk, closed: opt.id })}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${bulk.closed === opt.id ? 'bg-[#FF0059] text-white' : 'bg-white border border-gray-200 text-gray-700 hover:border-[#FF0059]'}`}
                    data-testid={`bulk-close-${opt.id || 'none'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-5 flex items-center gap-2 justify-end">
              <button onClick={() => setBulkOpen(false)} className="px-4 py-2 rounded-full border border-gray-200 text-sm font-bold">Cancel</button>
              <button onClick={submitBulk} className="px-4 py-2 rounded-full bg-[#FF0059] hover:bg-[#CC0047] text-white text-sm font-bold" data-testid="bulk-apply-btn">
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ========================================================================
// RATES & BOOST
// ========================================================================
export const RatesSection = ({ rooms, reloadRooms, user, reloadUser }) => {
  const [newRoom, setNewRoom] = useState({ name: '', base_price: '', total_rooms: '', max_guests: '2' });
  const [boostBudget, setBoostBudget] = useState(user?.boost_budget || 0);

  const createRoom = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/hotel/room-types`,
        { ...newRoom, base_price: Number(newRoom.base_price), total_rooms: Number(newRoom.total_rooms), max_guests: Number(newRoom.max_guests) },
        { withCredentials: true },
      );
      toast.success('Room type created · auto-approved');
      setNewRoom({ name: '', base_price: '', total_rooms: '', max_guests: '2' });
      reloadRooms();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const deleteRoom = async (id) => {
    if (!window.confirm('Delete this room type?')) return;
    try {
      await axios.delete(`${API}/hotel/room-types/${id}`, { withCredentials: true });
      toast.success('Deleted');
      reloadRooms();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const saveBoost = async () => {
    try {
      await axios.put(`${API}/hotel/boost`, { budget: Number(boostBudget) }, { withCredentials: true });
      toast.success('Boost budget saved');
      reloadUser();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="rates-section">
      <SectionTitle title="Rates & Boost" subtitle="Manage your room types, base rates, and visibility boost spend." />

      <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6" data-testid="rooms-list-card">
        <h3 className="text-lg font-black text-black mb-4">Your room types</h3>
        {rooms.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">No room types yet — add your first below.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {rooms.map((r) => (
              <li key={r._id} className="py-3 flex items-center justify-between gap-3" data-testid={`room-row-${r._id}`}>
                <div className="min-w-0">
                  <p className="font-bold text-black">{r.name}</p>
                  <p className="text-xs text-gray-500">{r.total_rooms} total rooms · max {r.max_guests} guest{r.max_guests > 1 ? 's' : ''}</p>
                </div>
                <p className="font-black text-black inline-flex items-center"><IndianRupee className="h-3.5 w-3.5" />{r.base_price}<span className="text-xs text-gray-400 ml-1">/night</span></p>
                <button onClick={() => deleteRoom(r._id)} className="p-1.5 rounded-full hover:bg-red-50 text-red-500" data-testid={`delete-room-${r._id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <form onSubmit={createRoom} className="bg-white rounded-2xl border border-gray-200 p-5 mb-6" data-testid="new-room-form">
        <h3 className="text-lg font-black text-black mb-4 flex items-center gap-2"><Plus className="h-4 w-4 text-[#FF0059]" /> Add room type</h3>
        <div className="grid sm:grid-cols-4 gap-3">
          <input type="text" placeholder="Name (Deluxe King…)" value={newRoom.name} onChange={(e) => setNewRoom({ ...newRoom, name: e.target.value })} required className="px-3 py-2 rounded-lg border border-gray-200 focus:border-[#FF0059] outline-none text-sm" data-testid="new-room-name" />
          <input type="number" placeholder="Base price ₹" value={newRoom.base_price} onChange={(e) => setNewRoom({ ...newRoom, base_price: e.target.value })} required className="px-3 py-2 rounded-lg border border-gray-200 focus:border-[#FF0059] outline-none text-sm" data-testid="new-room-price" />
          <input type="number" placeholder="Total rooms" value={newRoom.total_rooms} onChange={(e) => setNewRoom({ ...newRoom, total_rooms: e.target.value })} required className="px-3 py-2 rounded-lg border border-gray-200 focus:border-[#FF0059] outline-none text-sm" data-testid="new-room-total" />
          <input type="number" placeholder="Max guests" value={newRoom.max_guests} onChange={(e) => setNewRoom({ ...newRoom, max_guests: e.target.value })} required className="px-3 py-2 rounded-lg border border-gray-200 focus:border-[#FF0059] outline-none text-sm" data-testid="new-room-guests" />
        </div>
        <Button type="submit" className="mt-3 bg-[#FF0059] hover:bg-[#CC0047]" data-testid="add-room-btn">Add room type</Button>
      </form>

      <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="boost-card">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-5 w-5 text-[#FF0059]" />
          <h3 className="text-lg font-black text-black">Visibility Booster</h3>
        </div>
        <p className="text-sm text-gray-600 mb-3">Higher booster spend pushes your hotel up in search results.</p>
        <div className="flex items-center gap-3">
          <span className="text-sm font-bold text-gray-500">Monthly budget</span>
          <input type="number" value={boostBudget} onChange={(e) => setBoostBudget(e.target.value)} className="flex-1 sm:flex-none sm:w-44 px-3 py-2 rounded-lg border border-gray-200 focus:border-[#FF0059] outline-none text-sm" data-testid="boost-budget-input" />
          <Button onClick={saveBoost} className="bg-[#FF0059] hover:bg-[#CC0047]" data-testid="boost-save-btn">Save</Button>
        </div>
      </div>
    </div>
  );
};

// ========================================================================
// PROPERTY (editable hotel profile)
// ========================================================================
const AMENITIES = HOTEL_FACILITIES;

export const PropertySection = ({ user, reloadUser }) => {
  const [form, setForm] = useState({
    hotel_name: user.hotel_name || '',
    hotel_address: user.hotel_address || '',
    city_area: user.city_area || '',
    description: user.description || '',
    hotel_category: user.hotel_category || '',
    amenities: user.amenities || [],
    check_in_time: user.check_in_time || '14:00',
    check_out_time: user.check_out_time || '11:00',
  });
  const [saving, setSaving] = useState(false);

  const toggleAmenity = (a) => {
    setForm((p) => ({ ...p, amenities: p.amenities.includes(a) ? p.amenities.filter((x) => x !== a) : [...p.amenities, a] }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/hotel/property`, form, { withCredentials: true });
      toast.success('Property updated · auto-approved');
      reloadUser();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const Row = ({ label, children }) => (
    <div className="mb-4">
      <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1.5">{label}</label>
      {children}
    </div>
  );

  return (
    <div data-testid="property-section">
      <SectionTitle title="Property" subtitle="The information guests see on your listing." />
      <div className="bg-white rounded-2xl border border-gray-200 p-5 max-w-3xl">
        <Row label="Hotel name">
          <input type="text" value={form.hotel_name} onChange={(e) => setForm({ ...form, hotel_name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#FF0059] outline-none text-sm font-medium" data-testid="property-name-input" />
        </Row>
        <Row label="Address">
          <input type="text" value={form.hotel_address} onChange={(e) => setForm({ ...form, hotel_address: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#FF0059] outline-none text-sm" data-testid="property-address-input" />
        </Row>
        <Row label="Area / Neighbourhood">
          <input type="text" value={form.city_area} onChange={(e) => setForm({ ...form, city_area: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#FF0059] outline-none text-sm" data-testid="property-area-input" />
        </Row>
        <Row label="Category">
          <select value={form.hotel_category} onChange={(e) => setForm({ ...form, hotel_category: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#FF0059] outline-none text-sm bg-white" data-testid="property-category-select">
            <option value="">Select category…</option>
            <option value="1-star">1-Star · Basic budget</option>
            <option value="2-star">2-Star · Standard with en-suite</option>
            <option value="3-star">3-Star · Mid-market comfort</option>
            <option value="4-star">4-Star · Upscale with spa/pool</option>
            <option value="5-star">5-Star · Premium luxury</option>
          </select>
        </Row>
        <Row label="Description">
          <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#FF0059] outline-none text-sm resize-none" data-testid="property-description-input" />
        </Row>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1.5">Check-in time</label>
            <input type="time" value={form.check_in_time} onChange={(e) => setForm({ ...form, check_in_time: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#FF0059] outline-none text-sm font-bold" data-testid="property-checkin-input" />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1.5">Check-out time</label>
            <input type="time" value={form.check_out_time} onChange={(e) => setForm({ ...form, check_out_time: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#FF0059] outline-none text-sm font-bold" data-testid="property-checkout-input" />
          </div>
        </div>
        <Row label={`Hotel facilities (${form.amenities.length}/${AMENITIES.length})`}>
          <div className="flex flex-wrap gap-2">
            {AMENITIES.map((a) => (
              <button key={a} type="button" onClick={() => toggleAmenity(a)} className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${form.amenities.includes(a) ? 'bg-[#FF0059] text-white' : 'bg-white border border-gray-200 text-gray-700 hover:border-[#FF0059]'}`} data-testid={`property-amenity-${a.toLowerCase().replace(/\s+/g, '-')}`}>{a}</button>
            ))}
          </div>
        </Row>
        <Button onClick={save} disabled={saving} className="bg-[#FF0059] hover:bg-[#CC0047] mt-3" data-testid="property-save-btn">
          <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  );
};

// ========================================================================
// PHOTOS (multi-upload + auto-compress)
// ========================================================================
const FALLBACK = 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800';

export const PhotosSection = ({ user, reloadUser }) => {
  const [images, setImages] = useState(user.images || []);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState('');
  const fileRef = useRef(null);

  const persist = useCallback(async (next) => {
    setImages(next);
    try {
      await axios.put(`${API}/hotel/images`, { images: next }, { withCredentials: true });
      toast.success(`Saved ${next.length} photo${next.length === 1 ? '' : 's'} · auto-approved`);
      reloadUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  }, [reloadUser]);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    setProgress(`Compressing ${files.length} photo${files.length === 1 ? '' : 's'}…`);
    try {
      const results = await compressManyImages(files, { targetKb: 200, maxDim: 1600 });
      const dataUrls = results.map((r) => r.dataUrl);
      const next = [...images, ...dataUrls];
      const compressedAvg = Math.round(results.reduce((s, r) => s + r.sizeKb, 0) / results.length);
      const originalAvg = Math.round(results.reduce((s, r) => s + r.originalKb, 0) / results.length);
      setProgress(`Uploaded · avg ${compressedAvg}KB (from ${originalAvg}KB)`);
      await persist(next);
    } catch (err) {
      console.error('compress/upload failed', err);
      toast.error('Upload failed');
    }
    setUploading(false);
    setTimeout(() => setProgress(''), 3000);
    if (fileRef.current) fileRef.current.value = '';
  };

  const removeAt = async (idx) => {
    if (!window.confirm('Remove this photo?')) return;
    await persist(images.filter((_, i) => i !== idx));
  };

  return (
    <div data-testid="photos-section">
      <SectionTitle title="Photos" subtitle="Upload multiple photos at once — anything over 1 MB is auto-compressed under 200 KB." />
      <div className="inline-flex items-center gap-1.5 px-3 py-1 mb-5 rounded-full bg-green-50 border border-green-200 text-[11px] font-bold text-green-700">
        <CheckCircle2 className="h-3 w-3" /> Photo changes auto-approved · live instantly
      </div>
      <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-5">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFiles}
          className="hidden"
          data-testid="photos-file-input"
        />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="w-full border-2 border-dashed border-gray-200 hover:border-[#FF0059] rounded-2xl py-10 flex flex-col items-center gap-3 transition-colors disabled:opacity-60"
          data-testid="photos-upload-btn"
        >
          <Upload className="h-7 w-7 text-[#FF0059]" />
          <span className="font-bold text-black">{uploading ? progress || 'Uploading…' : 'Click to upload photos (multi-select)'}</span>
          <span className="text-xs text-gray-500">JPG, PNG · up to 20 MB each · auto-resized & compressed</span>
        </button>
        {progress && !uploading && (
          <p className="text-xs text-center text-gray-500 mt-2" data-testid="photos-progress">{progress}</p>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="photos-grid">
        {images.length === 0 ? (
          <div className="col-span-full bg-white border border-gray-200 rounded-2xl p-12 text-center">
            <ImageIcon className="h-10 w-10 mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-500">No photos uploaded yet. Add some to attract guests!</p>
          </div>
        ) : (
          images.map((src, idx) => (
            <div key={`${idx}-${src.slice(-20)}`} className="relative aspect-square rounded-xl overflow-hidden border border-gray-200 group" data-testid={`photo-tile-${idx}`}>
              <img src={src || FALLBACK} alt={`Property ${idx + 1}`} className="w-full h-full object-cover" />
              <button
                onClick={() => removeAt(idx)}
                className="absolute top-2 right-2 p-1.5 rounded-full bg-white/95 hover:bg-red-500 hover:text-white opacity-0 group-hover:opacity-100 transition-all"
                aria-label="Remove photo"
                data-testid={`photo-remove-${idx}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
